const DEMO_NETWORK_GUARD_KEY = Symbol.for('sgez.demo.network-guard')
const BLOCKED_XHR_KEY = Symbol('sgez.demo.blocked-xhr')

export function isDemoRuntimePath(pathname) {
  const value = String(pathname || '')
  return value === '/demo' || value.startsWith('/demo/')
}

export class DemoNetworkBlockedError extends Error {
  constructor(transport, target = '') {
    const suffix = target ? ` (${target})` : ''
    super(`Static demo blocked a ${transport} network request${suffix}.`)
    this.name = 'DemoNetworkBlockedError'
    this.transport = transport
    this.target = target
  }
}

function currentPath(scope) {
  return scope?.location?.pathname || scope?.window?.location?.pathname || ''
}

function targetText(value) {
  if (typeof value === 'string') return value
  if (value && typeof value.url === 'string') return value.url
  try {
    return String(value || '')
  } catch {
    return ''
  }
}

function createBlockedError(scope, transport, target) {
  const error = new DemoNetworkBlockedError(transport, targetText(target))
  try {
    const EventConstructor = scope?.CustomEvent
    if (typeof scope?.dispatchEvent === 'function' && typeof EventConstructor === 'function') {
      scope.dispatchEvent(new EventConstructor('sgez:demo-network-blocked', {
        detail: { transport, target: error.target },
      }))
    }
  } catch {
    // The request remains blocked even when diagnostic events are unavailable.
  }
  return error
}

function guardedConstructor(scope, Original, transport) {
  function GuardedTransport(...args) {
    if (isDemoRuntimePath(currentPath(scope))) {
      throw createBlockedError(scope, transport, args[0])
    }
    return Reflect.construct(Original, args, new.target === GuardedTransport ? Original : new.target)
  }

  Object.setPrototypeOf(GuardedTransport, Original)
  GuardedTransport.prototype = Original.prototype
  return GuardedTransport
}

function replaceMethod(target, key, value) {
  if (!target) return false
  try {
    Object.defineProperty(target, key, {
      configurable: true,
      writable: true,
      value,
    })
    return true
  } catch {
    try {
      target[key] = value
      return target[key] === value
    } catch {
      return false
    }
  }
}

/**
 * Installs a path-aware fail-closed boundary around browser transports.
 *
 * Static assets continue to load through the browser, while application-level
 * fetch/XHR/beacon/SSE/WebSocket traffic is impossible on every `/demo` route.
 * The wrappers remain installed so client-side navigation into the demo is
 * protected too; requests work normally again after leaving the demo path.
 */
export function installDemoNetworkGuard(scope = typeof window !== 'undefined' ? window : null) {
  if (!scope) return () => {}
  if (scope[DEMO_NETWORK_GUARD_KEY]) return scope[DEMO_NETWORK_GUARD_KEY].restore

  const restoreSteps = []
  const originalFetch = scope.fetch
  if (typeof originalFetch === 'function') {
    const guardedFetch = function guardedDemoFetch(input, init) {
      if (isDemoRuntimePath(currentPath(scope))) {
        return Promise.reject(createBlockedError(scope, 'fetch', input))
      }
      return originalFetch.call(this, input, init)
    }
    if (replaceMethod(scope, 'fetch', guardedFetch)) {
      restoreSteps.push(() => replaceMethod(scope, 'fetch', originalFetch))
    }
  }

  const xhrPrototype = scope.XMLHttpRequest?.prototype
  if (xhrPrototype && typeof xhrPrototype.open === 'function' && typeof xhrPrototype.send === 'function') {
    const originalOpen = xhrPrototype.open
    const originalSend = xhrPrototype.send
    const guardedOpen = function guardedDemoXhrOpen(method, url, ...rest) {
      if (isDemoRuntimePath(currentPath(scope))) {
        this[BLOCKED_XHR_KEY] = { method, url }
        throw createBlockedError(scope, 'XMLHttpRequest', url)
      }
      delete this[BLOCKED_XHR_KEY]
      return originalOpen.call(this, method, url, ...rest)
    }
    const guardedSend = function guardedDemoXhrSend(...args) {
      if (this[BLOCKED_XHR_KEY] || isDemoRuntimePath(currentPath(scope))) {
        throw createBlockedError(scope, 'XMLHttpRequest', this[BLOCKED_XHR_KEY]?.url)
      }
      return originalSend.apply(this, args)
    }
    if (replaceMethod(xhrPrototype, 'open', guardedOpen) && replaceMethod(xhrPrototype, 'send', guardedSend)) {
      restoreSteps.push(() => {
        replaceMethod(xhrPrototype, 'open', originalOpen)
        replaceMethod(xhrPrototype, 'send', originalSend)
      })
    }
  }

  const originalWebSocket = scope.WebSocket
  if (typeof originalWebSocket === 'function') {
    const guardedWebSocket = guardedConstructor(scope, originalWebSocket, 'WebSocket')
    if (replaceMethod(scope, 'WebSocket', guardedWebSocket)) {
      restoreSteps.push(() => replaceMethod(scope, 'WebSocket', originalWebSocket))
    }
  }

  const originalEventSource = scope.EventSource
  if (typeof originalEventSource === 'function') {
    const guardedEventSource = guardedConstructor(scope, originalEventSource, 'EventSource')
    if (replaceMethod(scope, 'EventSource', guardedEventSource)) {
      restoreSteps.push(() => replaceMethod(scope, 'EventSource', originalEventSource))
    }
  }

  const navigatorTarget = scope.navigator
  const originalSendBeacon = navigatorTarget?.sendBeacon
  if (typeof originalSendBeacon === 'function') {
    const guardedSendBeacon = function guardedDemoSendBeacon(url, data) {
      if (isDemoRuntimePath(currentPath(scope))) {
        createBlockedError(scope, 'sendBeacon', url)
        return false
      }
      return originalSendBeacon.call(this, url, data)
    }
    if (replaceMethod(navigatorTarget, 'sendBeacon', guardedSendBeacon)) {
      restoreSteps.push(() => replaceMethod(navigatorTarget, 'sendBeacon', originalSendBeacon))
    }
  }

  const state = {
    restore: () => {
      restoreSteps.reverse().forEach((restore) => restore())
      try {
        delete scope[DEMO_NETWORK_GUARD_KEY]
      } catch {
        // Test/runtime cleanup is best-effort; the transport boundary stays safe.
      }
    },
  }
  try {
    Object.defineProperty(scope, DEMO_NETWORK_GUARD_KEY, {
      configurable: true,
      value: state,
    })
  } catch {
    scope[DEMO_NETWORK_GUARD_KEY] = state
  }
  return state.restore
}

